import {useCallback, useEffect, useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {Competition, useSaveCompetition} from "@/hooks/useCompetitions";
import {notify} from "@/components/Notifications.tsx";
import {localInputToUtcIso, toLocalInput} from "@/lib/utils.ts";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {getErrMsg} from "@/lib/errors.ts";

export type Props = {
    open: boolean;
    initial?: Partial<Competition> | null;
    onClose: () => void;
};

const STATUS_OPTIONS = ["DRAFT", "OPEN", "CLOSED", "WINNER_ANNOUNCED"] as const;

function pickExistingImage(a?: unknown): { id?: string; url?: string } {
    const x = a as any;
    if (!x) return {};
    if (x.imageUrl) return {id: x?.id, url: x.imageUrl};
    return {};
}

export default function CompetitionUpsertDialog({open, initial, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    // Core fields
    const [title, setTitle] = useState(initial?.title ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [prizes, setPrizes] = useState<string[]>(
        Array.isArray((initial as any)?.prizes) ? ((initial as any).prizes as string[]) : []
    );
    const [newPrize, setNewPrize] = useState("");
    const [startAt, setStartAt] = useState<string>(initial?.startAt);
    const [endAt, setEndAt] = useState<string>(initial?.endAt);
    const [targetUrl, setTargetUrl] = useState(initial?.targetUrl ?? "");
    const [status, setStatus] = useState<string>((initial as any)?.status ?? "DRAFT");
    const [code, setCode] = useState(initial?.code ?? "");
    const [entryFee, setEntryFee] = useState<number | "">(initial?.entryFee ?? "");

    // File handling
    const [file, setFile] = useState<File | null>(null);
    const [uploadPct, setUploadPct] = useState(0);
    const [touched, setTouched] = useState(false);

    // Existing media / deletion
    const [removedExisting, setRemovedExisting] = useState(false);
    const existing = useMemo(() => pickExistingImage(initial), [initial]);
    const existingImageUrl = removedExisting ? undefined : existing.url;

    // Preview
    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    // Upload constraints
    const MAX_MB = 10;
    const ALLOWED_TYPES = useMemo(
        () => new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"]),
        []
    );
    const acceptAttr = useMemo(() => Array.from(ALLOWED_TYPES).join(","), [ALLOWED_TYPES]);

    const {mutateAsync: save, isPending} = useSaveCompetition();

    useEffect(() => {
        if (open) {
            setTitle(initial?.title ?? "");
            setDescription(initial?.description ?? "");
            setPrizes(Array.isArray((initial as any)?.prizes) ? ((initial as any).prizes as string[]) : []);
            setStartAt(toLocalInput(initial?.startAt));
            setEndAt(toLocalInput(initial?.endAt));
            setTargetUrl(initial?.targetUrl ?? "");
            setStatus((initial as any)?.status ?? "DRAFT");
            setCode(initial?.code ?? "");
            setEntryFee(initial?.entryFee ?? "");
            setNewPrize("");
            setFile(null);
            setUploadPct(0);
            setRemovedExisting(false);
            setTouched(false);
        }
    }, [open, initial]);

    const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (!ALLOWED_TYPES.has(f.type)) {
                notify.error(`Invalid file type. Allowed: ${Array.from(ALLOWED_TYPES).join(", ")}`);
                return;
            }
            if (f.size > MAX_MB * 1024 * 1024) {
                notify.error(`File too large. Max size is ${MAX_MB}MB.`);
                return;
            }
            setFile(f);
        },
        [ALLOWED_TYPES]
    );

    const trimmedPrizes = useMemo(
        () => prizes.map((p) => p.trim()).filter((p) => p.length > 0),
        [prizes]
    );

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!title.trim()) e.title = "Title is required.";
        if (!description.trim()) e.description = "Description is required.";
        if (trimmedPrizes.length === 0) e.prizes = "Please add at least one prize.";

        const needsFile = !isEdit || removedExisting;
        if (needsFile && !file) e.file = "Please choose an image file.";

        if (!startAt) e.startAt = "Start date is required.";
        if (!endAt) e.endAt = "End date is required.";

        if (startAt && endAt && new Date(startAt) > new Date(endAt)) {
            e.schedule = "End date must be after start date.";
        }

        if (!status) e.type = "Status is required.";

        return e;
    }, [title, description, startAt, endAt, file, existingImageUrl, trimmedPrizes.length]);

    const addPrize = () => {
        const v = newPrize.trim();
        if (!v) return;
        setPrizes((prev) => [...prev, v]);
        setNewPrize("");
    };

    const removePrize = (i: number) => {
        setPrizes((prev) => prev.filter((_, idx) => idx !== i));
    };

    const updatePrize = (i: number, val: string) => {
        setPrizes((prev) => prev.map((p, idx) => (idx === i ? val : p)));
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        try {
            await save({
                id: initial?.id,
                title,
                description,
                file,
                startAt: localInputToUtcIso(startAt),
                endAt: localInputToUtcIso(endAt),
                prizes: trimmedPrizes,
                targetUrl,
                status,
                code,
                entryFee,
                onUploadProgress: setUploadPct,
            } as any);
            setUploadPct(0);
            notify.success(`Competition ${isEdit ? "updated" : "created"} successfully.`);
            onClose();
        } catch (e) {
            notify.error(getErrMsg(e, `Failed to ${isEdit ? "update" : "create"} competition. Please try again.`));
        }
    };


    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit Competition" : "New Competition"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6">
                                {/* Title */}
                                <div>
                                    <label htmlFor="title"
                                           className="block text-sm font-medium text-blue-900">
                                        Title
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">The title of the competition.</p>
                                    <input
                                        id="title"
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title &&
                                        <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
                                </div>

                                {/* Description */}
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-blue-900">
                                        Description
                                    </label>
                                    <textarea
                                        id="description"
                                        rows={10}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        required
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.description && (
                                        <p className="mt-1 text-xs text-red-600">{errors.description}</p>
                                    )}
                                </div>

                                {/* --- Prizes --- */}
                                <div>
                                    <label className="block text-sm font-medium text-blue-900">Prizes</label>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Add the prizes that participants can win. (e.g., &quot;N$1,000
                                        voucher&quot;, &quot;iPhone 15&quot;)
                                    </p>

                                    {/* Existing items */}
                                    <div className="mt-3 space-y-2">
                                        {prizes.map((p, i) => (
                                            <div key={i} className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={p}
                                                    onChange={(e) => updatePrize(i, e.target.value)}
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                                    placeholder={`Prize #${i + 1}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removePrize(i)}
                                                    className="rounded-md px-2 py-1 text-sm text-gray-600 hover:text-red-600"
                                                    aria-label={`Remove prize ${i + 1}`}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Add new item */}
                                    <div className="mt-3 flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={newPrize}
                                            onChange={(e) => setNewPrize(e.target.value)}
                                            placeholder="Add a prize…"
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800"
                                        />
                                        <button
                                            type="button"
                                            onClick={addPrize}
                                            className="rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
                                            disabled={!newPrize.trim()}
                                        >
                                            Add
                                        </button>
                                    </div>

                                    {touched && errors.prizes &&
                                        <p className="mt-1 text-xs text-red-600">{errors.prizes}</p>}
                                </div>

                                {/* Schedule */}
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="startAt"
                                               className="block text-sm font-medium text-blue-900">Start</label>
                                        <input
                                            id="startAt"
                                            type="datetime-local"
                                            value={startAt}
                                            onChange={(e) => setStartAt(e.target.value)}
                                            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        />
                                        {touched && errors.startAt &&
                                            <p className="mt-1 text-xs text-red-600">{errors.startAt}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="endAt"
                                               className="block text-sm font-medium text-blue-900">End</label>
                                        <input
                                            id="endAt"
                                            type="datetime-local"
                                            value={endAt}
                                            onChange={(e) => setEndAt(e.target.value)}
                                            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        />
                                        {touched && errors.endAt &&
                                            <p className="mt-1 text-xs text-red-600">{errors.endAt}</p>}
                                    </div>
                                    {touched && errors.schedule &&
                                        <p className="text-xs text-red-600">{errors.schedule}</p>}
                                </div>

                                {/* Code */}
                                <div>
                                    <label htmlFor="code"
                                           className="block text-sm font-medium text-blue-900">
                                        Code (optional)
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">An optional code to identify the competition
                                        (e.g., for SMS entries).</p>
                                    <input
                                        id="code"
                                        type="text"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title && (
                                        <p className="mt-1 text-xs text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/*entryFee */}
                                <div>
                                    <label htmlFor="entryFee"
                                           className="block text-sm font-medium text-blue-900">
                                        Entry Fee (optional)
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">An optional entry fee amount in N$.</p>
                                    <input
                                        id="entryFee"
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        value={entryFee}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setEntryFee(val === "" ? "" : parseFloat(val));
                                        }}
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title && (
                                        <p className="mt-1 text-xs text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/* Image */}
                                <div>
                                    <label htmlFor="file" className="block text-sm font-medium text-blue-900">
                                        Image
                                    </label>

                                    {(previewUrl || existingImageUrl) && (
                                        <div className="mt-2">
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Competition image"
                                                className="h-full w-full rounded-md object-cover ring-1 ring-gray-200"
                                            />
                                        </div>
                                    )}
                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        <input
                                            id="file"
                                            name="file-upload"
                                            type="file"
                                            accept={acceptAttr}
                                            onChange={onPickFile}
                                            className="block w-full sm:w-auto text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                                        />
                                        {file && (
                                            <button
                                                type="button"
                                                onClick={() => setFile(null)}
                                                className="text-sm text-gray-600 hover:text-blue-900"
                                            >
                                                Clear selection
                                            </button>
                                        )}
                                    </div>

                                    {touched && errors.file && (
                                        <p className="mt-1 text-xs text-red-600">{errors.file}</p>
                                    )}

                                    {uploadPct > 0 && uploadPct < 100 && (
                                        <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-100">
                                            <div className="h-full bg-blue-900 transition-all"
                                                 style={{width: `${uploadPct}%`}}/>
                                        </div>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500">PNG, JPG up to {MAX_MB}MB.</p>
                                </div>

                                {/* Target URL */}
                                <div>
                                    <label htmlFor="link" className="block text-sm font-medium text-blue-900">
                                        Target URL (optional)
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">An optional URL to redirect to when users
                                        click on the competition.</p>
                                    <input
                                        id="link"
                                        type="text"
                                        value={targetUrl}
                                        onChange={(e) => setTargetUrl(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title && (
                                        <p className="mt-1 text-xs text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/* Status */}
                                <div>
                                    <label htmlFor="type"
                                           className="block text-sm font-medium text-blue-900">Status</label>
                                    <p className="mt-1 text-xs text-gray-500">The current status of the competition.</p>
                                    <div className="mt-2 relative">
                                        <select
                                            id="type"
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            className="w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        >
                                            {STATUS_OPTIONS.map((t) => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                        <ChevronDownIcon aria-hidden="true"
                                                         className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 size-5 text-gray-500 sm:size-4"/>
                                    </div>
                                    {touched && errors.type && (
                                        <p className="mt-1 text-xs text-red-600">{errors.type}</p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="mt-2 flex items-center justify-end gap-x-3">
                                    <button
                                        type="button" onClick={onClose}
                                        disabled={isPending}
                                        className="text-sm font-semibold text-blue-900"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isPending}
                                        className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
                                    >
                                        {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
